import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useDelayGate } from "@/hooks/useDelayGate";
import { LoadingShell } from "./LoadingShell";
import { TypewriterLine } from "./TypewriterLine";

interface PageLoaderProps {
  /** Message pool in the `loading` namespace (keys `pool.<name>.<n>`).
   * Omitted or unknown → the `generic` pool. */
  pool?: string;
  /** Production App variant: larger text, single-message pools
   * (PRODUCTION_DESIGN.md §5.4). */
  variant?: "inventory" | "production";
  className?: string;
}

const MAX_POOL_SIZE = 6;

function useLoadingMessages(pool: string | undefined): string[] {
  const { t, i18n } = useTranslation("loading");
  return useMemo(() => {
    const resolve = (p: string): string[] => {
      const out: string[] = [];
      for (let i = 1; i <= MAX_POOL_SIZE; i++) {
        // Static "pool." prefix keeps the whole family visible to
        // scripts/i18n-check.mjs's dynamic-prefix scan.
        if (!i18n.exists(`pool.${p}.${i}`, { ns: "loading" })) break;
        out.push(t(`pool.${p}.${i}`));
      }
      return out;
    };
    const fromPool = pool ? resolve(pool) : [];
    return fromPool.length > 0 ? fromPool : resolve("generic");
    // i18n.language dependency: pools re-resolve on locale switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, t, i18n, i18n.language]);
}

/**
 * The page-load state for full-page data loads (DESIGN.md §15.7).
 * Typewriter message + sliding bar, delay-gated so fast loads never see it.
 * Render it in the CONTENT REGION only — PageActionBar / titles / tabs stay
 * mounted above. Never-block contract: the parent unmounts it the instant
 * data arrives.
 */
export function PageLoader({ pool, variant = "inventory", className }: PageLoaderProps) {
  const messages = useLoadingMessages(pool);
  // Nothing (not even the typewriter state machine) runs during the gate
  // window — the loader body only mounts once the wait is real.
  const show = useDelayGate(400);
  if (!show) return null;

  return (
    <LoadingShell className={cn("motion-safe:animate-fade-in", className)}>
      <TypewriterLine messages={messages} size={variant === "production" ? "lg" : "sm"} />
    </LoadingShell>
  );
}
